import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArbeitsBereichBadge } from '@/domain/navigation/benachrichtigungen/components/antragsSheet/ArbeitsBereichBadge';
import { SelectTauschPartner } from '@/domain/navigation/benachrichtigungen/components/antragsSheet/SelectTauschPartner';
import { SheetContext } from '@/domain/navigation/benachrichtigungen/components/antragsSheet/SheetContext';
import { useUpdateSchichttauschantrag } from '@/domain/navigation/benachrichtigungen/hooks/useUpdateSchichttauschAntrag';
import { Mitarbeiter } from '@/domain/navigation/benachrichtigungen/types/Benachrichtungen';
import { SchichttauschAntrag } from '@/domain/navigation/benachrichtigungen/types/SchichttauschAntrag';
import { Navigation_SchichttauschantragStatus } from '@/gql/graphql';
import { toast } from '@/hooks/use-toast';
import { faCheck, faXmarkLarge } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import { useContext } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import z from 'zod';

// eslint-disable-next-line max-lines-per-function
export function AntragsFormular({ antrag }: { antrag: SchichttauschAntrag }) {
  const { setOpen } = useContext(SheetContext);
  const [update, result] = useUpdateSchichttauschantrag();
  const { t } = useTranslation();

  const formSchema = z
    .object({
      tauschPartner: z.custom<Mitarbeiter>(),
      bemerkung: z.string(),
    })
    .refine((data) => data.tauschPartner !== null, {
      message: t(
        'domain.navigation.benachrichtigungen.schichttauschAntraege.dialog.form.errors.tauschPartnerRequired',
      ),
      path: ['tauschPartner'],
    });

  const form = useForm<{
    tauschPartner: Mitarbeiter | null;
    bemerkung: string | null;
  }>({
    mode: 'onChange',
    resolver: zodResolver(formSchema),
    defaultValues: {
      tauschPartner: null,
      bemerkung: '',
    },
  });

  const onSubmit = async () => {
    const result = await update({
      input: {
        schichttauschAntragId: antrag.id,
        status: Navigation_SchichttauschantragStatus.Genehmigt,
        tauschPartnerId: form.getValues().tauschPartner?.id,
        bemerkungManager: form.getValues().bemerkung,
      },
    });
    if (result.data.navigation_updateSchichttauschAntrag.error) {
      toast({
        title: result.data.navigation_updateSchichttauschAntrag.error.message,
      });
    }
    if (result.data.navigation_updateSchichttauschAntrag.success) {
      toast({
        title: t(
          'domain.navigation.benachrichtigungen.schichttauschAntraege.dialog.form.toast.genehmigen.title',
        ),
        description: t(
          'domain.navigation.benachrichtigungen.schichttauschAntraege.dialog.form.toast.genehmigen.description',
        ),
      });
      setOpen(false);
    }
  };
  const handleReject = async () => {
    const result = await update({
      input: {
        schichttauschAntragId: antrag.id,
        status: Navigation_SchichttauschantragStatus.Abgelehnt,
        bemerkungManager: form.getValues().bemerkung,
      },
    });

    if (result.data.navigation_updateSchichttauschAntrag.error) {
      toast({
        title: result.data.navigation_updateSchichttauschAntrag.error.message,
      });
    }
    if (result.data.navigation_updateSchichttauschAntrag.success) {
      toast({
        title: t(
          'domain.navigation.benachrichtigungen.schichttauschAntraege.dialog.form.toast.ablehnen.title',
        ),
        description: t(
          'domain.navigation.benachrichtigungen.schichttauschAntraege.dialog.form.toast.ablehnen.description',
        ),
      });
      setOpen(false);
    }
  };
  return (
    <Form {...form}>
      <form
        id="updateTauschantrag"
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-6 sm:w-80">
        <div className="flex flex-col gap-2">
          <Label className="text-neutrals-900 text-xs font-bold">
            {t(
              'domain.navigation.benachrichtigungen.schichttauschAntraege.dialog.form.arbeitsbereich',
            )}
          </Label>
        </div>
        <ArbeitsBereichBadge arbeitsbereich={antrag.schicht.arbeitsbereich} />
        <div className="flex flex-col gap-2">
          <Label className="text-neutrals-900 text-xs font-bold">
            {' '}
            {t(
              'domain.navigation.benachrichtigungen.schichttauschAntraege.dialog.form.datum',
            )}
          </Label>

          <span className="text-neutrals-900 text-sm">
            {dayjs(antrag.schicht.beginn.toDate())
              .locale('de')
              .format('ddd, DD. MMMM, HH:mm')}
            {' - '}
            {antrag.schicht.ende.format('HH:mm')} Uhr
          </span>
        </div>
        <FormField
          control={form.control}
          name="tauschPartner"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t(
                  'domain.navigation.benachrichtigungen.schichttauschAntraege.dialog.form.tauschPartnerSelect.label',
                )}
              </FormLabel>
              <FormControl>
                <SelectTauschPartner
                  antrag={antrag}
                  value={field.value}
                  onSelect={(m) => field.onChange(m)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex flex-col gap-2">
          <FormField
            control={form.control}
            name="bemerkung"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {' '}
                  {t(
                    'domain.navigation.benachrichtigungen.schichttauschAntraege.dialog.form.deineBemerkung',
                  )}
                </FormLabel>
                <FormControl>
                  <Textarea
                    className=" h-24"
                    maxLength={250}
                    value={field.value}
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex flex-row gap-4 w-full justify-end">
          <Button
            variant="destructive"
            className="flex gap-2"
            onClick={handleReject}
            disabled={result.loading}>
            <FontAwesomeIcon icon={faXmarkLarge} />
            {t(
              'domain.navigation.benachrichtigungen.schichttauschAntraege.dialog.form.ablehnen',
            )}
          </Button>
          <Button
            className="flex gap-2"
            type="submit"
            form="updateTauschantrag"
            disabled={result.loading || !form.formState.isValid}>
            <FontAwesomeIcon icon={faCheck} />
            {t(
              'domain.navigation.benachrichtigungen.schichttauschAntraege.dialog.form.genehmigen',
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}
